import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { apiGet, apiPost } from "../../api/client";
import type { PlayerResponse } from "../../api/legacyTypes";
import { AppShell } from "../../app/AppShell";
import { CountryMark } from "../../components/CountryMark";
import { EntityLink } from "../../components/EntityLink";
import { ImageLightbox } from "../../components/ImageLightbox";
import { LocalDate, LocalDateRange } from "../../components/LocalDateTime";
import { TierFilter } from "../../components/TierFilter";
import { displayPersonName } from "../../utils/personName";

export function PlayerPage() {
  const { wiki = "trackmania", slug = "" } = useParams();
  const queryClient = useQueryClient();
  const [achievementTiers, setAchievementTiers] = useState(new Set<string>());
  const [historyTiers, setHistoryTiers] = useState(new Set<string>());
  const query = useQuery({
    queryKey: ["player", wiki, slug],
    queryFn: ({ signal }) => apiGet<PlayerResponse>(`/api/v1/players/${wiki}/${slug}`, signal),
  });
  const refresh = useMutation({
    mutationFn: () => apiPost<{ status: string }>(`/api/players/${wiki}/${slug}/refresh?force=true`),
    onSuccess: () => window.setTimeout(() => void queryClient.invalidateQueries({ queryKey: ["player", wiki, slug] }), 1500),
  });
  if (query.isPending) return <AppShell selectedGame={wiki}><p className="state-panel">Loading player…</p></AppShell>;
  if (query.isError) return <AppShell selectedGame={wiki}><p className="state-panel state-error">Player details are not cached yet.</p></AppShell>;
  const { player, tournaments, mediaUrl } = query.data;
  const results = [...player.results]
    .filter((result) => achievementTiers.size === 0 || achievementTiers.has(result.tier))
    .sort((left, right) => right.date.localeCompare(left.date));
  const history = [...player.teamHistory]
    .filter((period) => historyTiers.size === 0 || player.results.some((result) => result.organization === period.name && historyTiers.has(result.tier)))
    .sort((left, right) => right.lastResult.localeCompare(left.lastResult));
  return (
    <AppShell selectedGame={wiki}>
      <section className="entity-hero" data-game={wiki}>
        <ImageLightbox className="entity-portrait" src={mediaUrl} alt={player.handle} />
        <div>
          <p className="eyebrow">{wiki} player</p>
          <h1>{player.handle}</h1>
          <p className="entity-real-name">{player.realName ? displayPersonName(player.realName) : "Real name not listed"}</p>
          <div className="entity-meta"><span><CountryMark country={player.country} />{player.country || "Country unknown"}</span><span>{player.status || "Status unknown"}</span></div>
          {player.organizationName && <EntityLink gameId={wiki} kind="team" name={player.organizationName} entityId={player.organizationId} />}
        </div>
        <button className="button-secondary" type="button" disabled={refresh.isPending} onClick={() => refresh.mutate()}>{refresh.isPending ? "Refresh queued…" : refresh.isSuccess ? "Refresh started" : "Refresh this player"}</button>
      </section>
      <section className="metric-grid">
        <article><small>Recorded results</small><strong>{player.results.length}</strong></article>
        <article><small>Organizations</small><strong>{player.teamHistory.length}</strong></article>
        <article><small>Tournaments</small><strong>{tournaments.length}</strong></article>
      </section>
      <section className="content-panel">
        <p className="eyebrow">Career timeline</p><div className="section-title-row"><h2>Organization history</h2><TierFilter selected={historyTiers} onChange={setHistoryTiers} label="Filter organization history by tournament tier" /></div>
        <div className="entity-card-grid">
          {history.map((period) => <article key={`${period.id}-${period.firstResult}`}><EntityLink gameId={wiki} kind="team" name={period.name} entityId={period.id} /><small><LocalDateRange start={period.firstResult} end={period.lastResult} /> · {period.appearances} results</small></article>)}
        </div>
      </section>
      <section className="content-panel">
        <p className="eyebrow">Results</p><div className="section-title-row"><h2>Achievements</h2><TierFilter selected={achievementTiers} onChange={setAchievementTiers} label="Filter achievements by tournament tier" /></div>
        <div className="result-list">
          {results.map((result, index) => <article key={`${result.date}-${result.tournament}-${index}`}><strong>{result.placement}</strong><span>{result.tournament}</span><span>{result.organization}</span><LocalDate value={result.date} /><em>{result.prize}</em></article>)}
        </div>
      </section>
    </AppShell>
  );
}
